import type { Swiper, SwiperEvents } from "swiper/types";

// Swiper implements this method at runtime but omits it from its public types.
type TransitionSwiper = Swiper & {
    setTransition: (duration: number) => void;
};

type SlideTo = (
    index: number,
    speed?: number,
    runCallbacks?: boolean,
    internal?: boolean,
) => boolean;

/**
 * Distributes consecutive auto-width slides across the available slider width.
 * It only considers complete slides from the destination position, so the next
 * slide starts outside the viewport instead of appearing partially at the end.
 */
export const DistributedSlidesModule = ({
    swiper,
    on,
}: {
    swiper: Swiper;
    on: <E extends keyof SwiperEvents>(
        event: E,
        handler: SwiperEvents[E],
    ) => void;
}) => {
    let frame = 0;
    let baseSpaceBetween = 0;
    let distributedSpaceBetween = -1;
    let initialized = false;
    let sliderWidth = 0;
    let slider: HTMLElement | null = null;
    let optimalSlideWidth = 0;

    const originalSlideTo = swiper.slideTo.bind(swiper) as SlideTo;

    function normalizeTransitionSpeed(baseDuration: number) {
        if (!slider || !optimalSlideWidth || !baseDuration) {
            return;
        }

        const activeSlide = swiper.slides[swiper.activeIndex] as
            HTMLElement | undefined;
        if (!activeSlide) {
            return;
        }

        const viewportBounds = swiper.el.getBoundingClientRect();
        const slideBounds = activeSlide.getBoundingClientRect();
        const distance = Math.abs(
            swiper.rtlTranslate
                ? viewportBounds.right - slideBounds.right
                : slideBounds.left - viewportBounds.left,
        );
        const scale = Number.parseFloat(
            getComputedStyle(slider).getPropertyValue(
                "--slider-auto-width-scale",
            ),
        );
        const referenceDistance =
            optimalSlideWidth * (Number.isFinite(scale) ? scale : 1);

        if (
            !Number.isFinite(distance) ||
            distance <= 0 ||
            referenceDistance <= 0
        ) {
            return;
        }

        (swiper as TransitionSwiper).setTransition(
            (baseDuration * distance) / referenceDistance,
        );
    }

    function distribute(destinationIndex: number) {
        const slides = Array.from(swiper.slides) as HTMLElement[];
        // Keep a tiny buffer for sub-pixel differences between Swiper's
        // calculated grid and the browser's rendered slide boundaries.
        const availableWidth = swiper.el.getBoundingClientRect().width - 2;
        const startIndex = Math.max(
            0,
            Math.min(Math.trunc(destinationIndex), slides.length - 1),
        );

        if (!availableWidth || !slides[startIndex]) {
            return;
        }

        let slidesWidth = 0;
        let numberOfSlides = 0;

        // Swiper moves slide elements in loop mode instead of cloning them.
        // Only elements after the destination are actually available on the
        // right-hand side of the current row. Counting wrapped array entries
        // here would reserve space for logos that remain before the viewport.
        for (let index = startIndex; index < slides.length; index++) {
            const width = slides[index].getBoundingClientRect().width;
            const minimumGapsWidth = numberOfSlides * baseSpaceBetween;

            if (
                numberOfSlides &&
                slidesWidth + minimumGapsWidth + width > availableWidth
            ) {
                break;
            }

            slidesWidth += width;
            numberOfSlides++;
        }

        const spaceBetween =
            numberOfSlides > 1
                ? Math.max(
                      baseSpaceBetween,
                      (availableWidth - slidesWidth) / (numberOfSlides - 1),
                  )
                : baseSpaceBetween;

        if (Math.abs(spaceBetween - distributedSpaceBetween) >= 0.5) {
            distributedSpaceBetween = spaceBetween;
            swiper.params.spaceBetween = spaceBetween;
        }

        swiper.updateSlides();

        if (swiper.params.loop) {
            // Loop mode reorders the slide elements. Preserve every resulting
            // slide position as a destination and keep it one pixel inside
            // the viewport to avoid sub-pixel clipping on either edge.
            swiper.snapGrid = swiper.slidesGrid.map((position) => position - 1);
        }
    }

    // Calculate the destination layout before Swiper reads snapGrid inside
    // slideTo(). The gap and transform are therefore painted as one normal
    // transition, with no visible correction after transitionEnd.
    swiper.slideTo = ((
        index: number,
        speed?: number,
        runCallbacks?: boolean,
        internal?: boolean,
    ) => {
        const transitionDuration = speed ?? swiper.params.speed ?? 0;

        // Autoplay marks its regular transitions as internal, as do Swiper's
        // zero-duration loop corrections. Only the latter must be skipped.
        if (initialized && (!internal || transitionDuration > 0)) {
            distribute(index);
        }

        const didSlide = originalSlideTo(index, speed, runCallbacks, internal);

        // Auto-width slides travel different distances. Scale the duration to
        // that distance so each transition keeps a consistent visual speed.
        if (initialized && didSlide) {
            normalizeTransitionSpeed(transitionDuration);
        }

        return didSlide;
    }) as Swiper["slideTo"];

    function scheduleCurrentLayout(force = false) {
        const currentWidth = swiper.el.getBoundingClientRect().width;

        // Swiper can emit resize while its slides are being updated even when
        // the viewport itself did not change. Reacting to those events creates
        // a self-sustaining update loop and cancels active transitions.
        if (!force && Math.abs(currentWidth - sliderWidth) < 0.5) {
            return;
        }

        sliderWidth = currentWidth;
        cancelAnimationFrame(frame);

        const updateWhenSettled = () => {
            if (swiper.animating) {
                frame = requestAnimationFrame(updateWhenSettled);
                return;
            }

            distribute(swiper.activeIndex);
            originalSlideTo(swiper.activeIndex, 0, false, true);
        };

        frame = requestAnimationFrame(updateWhenSettled);
    }

    on("beforeInit", () => {
        baseSpaceBetween = Number(swiper.params.spaceBetween) || 0;
        slider = swiper.el.closest<HTMLElement>("[slider]");
        optimalSlideWidth = Number(slider?.dataset.slidesOptimalWidth) || 0;
    });
    on("afterInit", () => {
        initialized = true;
        scheduleCurrentLayout(true);
    });
    on("resize", () => scheduleCurrentLayout());
};
